import { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { dateFnsLocales } from 'lib/utils/getDateFnsLocales';
import { Chip, JamIcon } from 'components/ui';
import { getMainComponent } from 'lib/utils/metricComponents';
import { useLazyQuery } from '@apollo/client';
import { GET_PRACTITIONER_BY_ID } from 'pages/PatientRecordPage/gqlSchemes/getPreactitionerById';
import { QuestionnaireResponseHistoryItem } from 'pages/PatientRecordPage/gqlSchemes/getQuestionnaireResponseHistory';
import { formatNumber } from 'lib/utils';

interface VersionHistoryDropdownProps {
  versionHistory: QuestionnaireResponseHistoryItem[];
  metricDefinitionId: string;
  onSeeAnswers: (
    response: QuestionnaireResponseHistoryItem,
    metricDefinitionId: string,
    isLatestVersion?: boolean
  ) => void;
}

// Component that fetches and displays author name
const AuthorName = ({ authorId }: { authorId: string }) => {
  const { t } = useTranslation();
  const [loadPractitioner, { data, loading }] = useLazyQuery(
    GET_PRACTITIONER_BY_ID,
    {
      variables: { id: authorId },
      fetchPolicy: 'no-cache',
    }
  );

  useEffect(() => {
    if (authorId) {
      loadPractitioner();
    }
  }, [authorId, loadPractitioner]);

  if (loading) return <span>...</span>;

  const practitioner = data?.user?.dashboard?.practitionerById;
  if (!practitioner) return <span>{t('Unknown')}</span>;

  return <span>{`${practitioner.firstName} ${practitioner.lastName}`}</span>;
};

const VersionHistoryDropdown = ({
  versionHistory,
  metricDefinitionId,
  onSeeAnswers,
}: VersionHistoryDropdownProps) => {
  const [expanded, setExpanded] = useState(false);
  const { t, i18n } = useTranslation();
  const locale = dateFnsLocales[i18n.language as keyof typeof dateFnsLocales];

  // Don't render if no history items
  if (!versionHistory || versionHistory.length === 0) {
    return null;
  }

  const getScore = (item: QuestionnaireResponseHistoryItem) => {
    if (item.mainScoreMetric?.metricDefinitionId === metricDefinitionId) {
      return (
        getMainComponent(item.mainScoreMetric?.components)?.quantityValue
          ?.value || '-'
      );
    }
    const subScore = item.subScoreMetrics?.find(
      (subscore) => subscore?.metricDefinitionId === metricDefinitionId
    );
    return getMainComponent(subScore?.components)?.quantityValue?.value || '-';
  };

  const getDate = (item: QuestionnaireResponseHistoryItem) => {
    const createdAt =
      item.authored ??
      (item.mainScoreMetric?.metricDefinitionId === metricDefinitionId
        ? item.mainScoreMetric?.createdAt
        : item.subScoreMetrics?.find(
            (subscore) => subscore?.metricDefinitionId === metricDefinitionId
          )?.createdAt);

    if (!createdAt) return '-';
    return format(parseISO(createdAt), 'PPPp', { locale });
  };

  return (
    <SContainer>
      <SHeader onClick={() => setExpanded(!expanded)}>
        <Typography variant="h6">
          {t('Show X Previous Versions', { number: versionHistory.length })}
        </Typography>
        <IconButton size="small">
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </SHeader>

      <Collapse in={expanded}>
        <SVersionList>
          {versionHistory.map((item, index) => (
            <SVersionItem key={item.id + '-' + item.version}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box flex={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('Version')} {item.version}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2} mt={1}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      bgcolor="#f6f6f6"
                      p="10px"
                      borderRadius="5px"
                    >
                      <Typography variant="h4">
                        {formatNumber(getScore(item), 0)}
                      </Typography>
                      <Typography variant="h5" color="textSecondary">
                        {t('Score')}
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      <Typography variant="body2">{getDate(item)}</Typography>
                    </Box>
                    {item.author && (
                      <>
                        <Divider orientation="vertical" flexItem />
                        <Box>
                          <Typography variant="body2">
                            <AuthorName authorId={item.author.id} />
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                  {item.reasonForAmendment && (
                    <Box mt={1}>
                      <Typography variant="caption" color="textSecondary">
                        {t('Reason')}: {item.reasonForAmendment}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box>
                  <Chip
                    label={t('Old')}
                    color="primary"
                    $color="#f8f8f8"
                    $backgroundColor="#777777"
                    style={{ marginRight: '10px' }}
                  />
                  <Button
                    style={{ borderRadius: '25px', padding: '4px 12px' }}
                    startIcon={
                      <JamIcon
                        size={20}
                        icon="document"
                        color="inherit"
                        style={{}}
                      />
                    }
                    onClick={() => onSeeAnswers(item, metricDefinitionId)}
                  >
                    {t('See answers')}
                  </Button>
                </Box>
              </Box>
              {index < versionHistory.length - 1 && <Divider sx={{ mt: 2 }} />}
            </SVersionItem>
          ))}
        </SVersionList>
      </Collapse>
    </SContainer>
  );
};

const SContainer = styled.div`
  margin-top: 16px;
  border: 1px solid ${(props) => props.theme.palette.grey[200]};
  border-radius: 8px;
  overflow: hidden;
`;

const SHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  background-color: ${(props) => props.theme.palette.grey[50]};

  &:hover {
    background-color: ${(props) => props.theme.palette.grey[100]};
  }
`;

const SVersionList = styled.div`
  padding: 16px;
`;

const SVersionItem = styled.div`
  padding: 8px 0;
`;

export default VersionHistoryDropdown;
